import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface Props {
  currentModule: string;
  isFirstVisit: boolean;
}

const WELCOME: Record<string, string> = {
  home: 'Cześć! 👋 Jestem asystentem OPENkontakt. Oto za co odpowiada każdy moduł:\n\n' +
    '✉️ **Kontakt** — cała korespondencja z klientami: wiadomości, dyskusje i reklamacje, oceny kupujących. Możesz też podłączyć swoje skrzynki mailowe i mieć wszystko w jednym miejscu.\n' +
    '🏪 **Oferty** — tworzenie i edycja ofert, ceny, stany magazynowe, przedsprzedaż, wyróżnienia.\n' +
    '📦 **Zwroty** — przesyłki **zwrotne** od klientów: obsługa zwrotów towarów i reklamacji bezpośrednio z Allegro.\n' +
    '💰 **Odrzucone** — odrzucone wnioski o zwrot prowizji: odwołania i dodatkowa weryfikacja.\n' +
    '🚚 **Przesyłki** — przesyłki **wysłane** do klientów. Po numerze przesyłki znajdziesz zamówienie: dane odbiorcy, zawartość, status śledzenia i powiązane rozmowy.\n' +
    '🤖 **AI OPEN** — asystent sprzedaży dla Twojego sklepu www: szuka produktów, doradza klientom i przyjmuje zamówienia.\n' +
    '⚙️ **Ustawienia** — konta Allegro, parametry AI, baza wiedzy, auto-odpowiedzi, użytkownicy.\n\n' +
    'Wybierz moduł z menu lub zapytaj mnie o cokolwiek. Jak mogę pomóc?',
  messages: 'Jesteś na liście wiadomości od kupujących Allegro. Kliknij dowolną wiadomość żeby ją otworzyć — AI automatycznie zaproponuje odpowiedź. Możesz filtrować nieprzeczytane lub szukać po loginie kupującego.',
  'messages:thread': 'Masz otwartą wiadomość od kupującego. Kliknij "Generuj odpowiedź AI" — AI przygotuje odpowiedź na podstawie bazy wiedzy. Możesz ją edytować przed wysłaniem. Jeśli klient pisze obcym językiem, odpowiedź zostanie automatycznie przetłumaczona.',
  issues: 'Jesteś na liście dyskusji i reklamacji z Allegro. Dyskusje (💬) to spory, reklamacje (⚠️) wymagają decyzji. Kliknij pozycję żeby ją otworzyć i odpowiedzieć.',
  'issues:dispute': 'Masz otwartą dyskusję z kupującym. Możesz odpowiedzieć na wiadomość, wygenerować odpowiedź przez AI lub oznaczyć jako zakończoną.',
  'issues:claim': 'Masz otwartą reklamację. Możesz: odpowiedzieć kupującemu, użyć przycisku "Żądaj zwrotu" lub kliknąć "Decyzja reklamacyjna". Decyzja jest nieodwracalna.',
  ratings: 'Jesteś w module Oceny. Czerwone oceny (negatywne) wymagają odpowiedzi — masz na to 90 dni.',
  offers: 'Jesteś w module Oferty. Możesz dodawać nowe oferty, edytować istniejące, zmieniać ceny i stany zbiorczo oraz zarządzać wyróżnieniami. Edycja oferty (✏️) pozwala zmienić: tytuł, cenę, stan, czas wysyłki, opis, parametry oraz zdjęcia (dodawanie, usuwanie, zmiana kolejności i zdjęcia głównego).',
  'refund-claims': 'Jesteś w module Odrzucone prowizje. Możesz złożyć odwołanie automatycznie. Najpierw synchronizuj wnioski.',
  shipments: 'Jesteś w module Przesyłki. Wpisz numer przesyłki (list przewozowy), a znajdę powiązane zamówienie: dane kupującego, produkty, kwoty, formę dostawy i adres, fakturę lub paragon oraz pełną historię śledzenia. Numer zamówienia, login, SKU i telefon skopiujesz jednym kliknięciem, a przyciski Wiadomości i Zgłoszenia otworzą rozmowy z tym klientem pod spodem.',
  apilo: 'Jesteś w module Wyszukiwarka Apilo. Wpisz cokolwiek, co znasz o zamówieniu: nazwisko, miasto, telefon, numer zamówienia, login Allegro, e-mail, SKU lub EAN — także z danych do wysyłki. Możesz łączyć słowa spacją lub użyć przedrostków (np. sku:1331 Leszno) i zawęzić wyniki datami. Pod znalezionym zamówieniem masz przyciski Zwrot, Przesyłka i Apilo CRM.',
  aiopen: 'Jesteś w module AI OPEN. Wymaga własnego klucza API skonfigurowanego w Ustawieniach.',
  settings: 'Jesteś w Ustawieniach. Wybierz zakładkę z lewej.',
  'settings:allegro': 'Zakładka Konta Allegro. Tu dodajesz konta przez OAuth.',
  'settings:ai': 'Zakładka Konfiguracja AI. Możesz podłączyć własny klucz API.',
  'settings:knowledge': 'Zakładka Baza wiedzy. Dodaj FAQ swojego sklepu.',
  'settings:profile': 'Zakładka Moje konto. Możesz zmienić hasło.',
  'settings:templates': 'Zakładka Wzory odpowiedzi. Używaj ich w module Dyskusje.',
  'settings:shortcuts': 'Zakładka Skróty. Wpisz # w polu odpowiedzi aby wkleić skrót.',
  'settings:users': 'Zakładka Użytkownicy. Możesz dodać agentów.',
  'settings:asystent': 'Ustawienia asystenta AI. Możesz włączyć lub wyłączyć asystenta osobno dla mobile i desktop. Na mobile możesz przesuwać ikonkę.',
  'refund-claims:appealed': 'Odwołane wnioski. Allegro rozpatruje je zazwyczaj w ciągu 14 dni.',
  'refund-claims:resolved': 'Rozwiązane wnioski o zwrot prowizji. Możesz je przywrócić do aktywnych.',
  'refund-claims:passwords': 'Hasła do kont Allegro wymagane do automatycznego składania odwołań.',
  'refund-claims:import': 'Dodawanie wniosków ręcznie. Wklej loginy kupujących jeden per linia.',
  returns: 'Jesteś w module Zwroty. Szukaj po loginie, numerze zwrotu, przesyłki lub telefonie.',
  'returns:refunds': 'Zakładka Wydruki. Lista zwrotów płatności z Allegro z możliwością pobrania ZIP.',
  'offers:promos': 'Zakładka Wyróżnienia. Widzisz aktywne promowane oferty.',
  'settings:offers': 'Zakładka Oferty. Konfiguracja ustawień ofert Allegro.',
  'settings:hours': 'Zakładka Godziny pracy. Ustaw godziny i treść auto-odpowiedzi.',
  'email': 'Jesteś w module Email. Widzisz skrzynki email klientów. Kliknij wątek żeby go otworzyć. Możesz filtrować nowe/wszystkie, szukać po nadawcy lub temacie. Przycisk ✉️ otwiera formularz nowej wiadomości.',
  'email:thread': 'Masz otwarty wątek email. Możesz odpowiedzieć po polsku — AI automatycznie przetłumaczy na język klienta. Przycisk ✦ AI generuje odpowiedź, 🌐 PL tłumaczy przychodzące wiadomości, 📎 dodaje załączniki. Przycisk 🕐 Historia pokazuje historię obsługi wątku.',
  'email:compose': 'Tworzysz nową wiadomość email. Wybierz konto wysyłające, wpisz odbiorcę, temat i treść po polsku. Wybierz język — AI automatycznie przetłumaczy. Przycisk ✦ AI generuje treść na podstawie tematu.',
  'settings:imap': 'Zakładka Konta Email. Tu dodajesz skrzynki IMAP/SMTP. Po wpisaniu adresu email i hasła kliknij "Testuj połączenie" — system automatycznie wykryje serwery IMAP i SMTP. Możesz też ustawić reguły automatyczne dla każdego konta.',
  'settings:imap:rules': 'Reguły automatyczne email. Możesz ustawić warunki (nadawca/temat) i akcję: przydzielenie do agenta lub automatyczne zakończenie. Reguły są stosowane do nowych emaili przy każdej synchronizacji.',
};

const SUGGESTIONS: Record<string, string[]> = {
  home: ['Od czego powinienem zacząć konfigurację?', 'Jak krok po kroku podłączyć konto Allegro?', 'Czy mogę podpiąć wiele kont Allegro?', 'Jak uzupełnić bazę wiedzy dla AI?', 'Czy system sam pobierze moje oferty?'],
  shipments: ['Do czego służą „Przesyłki”?', 'Jak znaleźć zamówienie po numerze?', 'Skąd są statusy przesyłki?', 'Czemu brak starszej przesyłki?'],
  apilo: ['Po czym mogę szukać zamówień?', 'Jak zawęzić wyniki (przedrostki, daty)?', 'Do czego służy Apilo CRM?', 'Co robią przyciski Zwrot i Przesyłka?'],
  messages: ['Co oznacza zakładka Nowe i jak działa Zakończ?', 'Do czego służy przycisk Zamówienia?', 'Czym różni się Wyślij od Wyślij i ucz AI?', 'Jak wysłać klientowi link do oferty?', 'W jakim języku odpisywać klientom z zagranicy?', 'Jak działa przydzielanie maili do pracowników?'],
  'messages:thread': ['Jak wygenerować odpowiedź AI?', 'Jak działa tłumaczenie?', 'Jak dodać załącznik?'],
  issues: ['Jak otworzyć dyskusję?', 'Czym różni się dyskusja od reklamacji?', 'Jak filtrować reklamacje?'],
  'issues:dispute': ['Jak odpowiedzieć w dyskusji?', 'Jak zakończyć dyskusję?', 'Jak użyć wzoru odpowiedzi?'],
  'issues:claim': ['Jak użyć Decyzji reklamacyjnej?', 'Jak żądać zwrotu towaru?', 'Jak odpowiedzieć przez AI?'],
  ratings: ['Jak odpowiedzieć na ocenę?', 'Jak usunąć złą ocenę?', 'Ile czasu mam na odpowiedź?'],
  offers: ['Jak filtrować oferty?', 'Jakie są akcje masowe?', 'Co robi „Synchronizuj oferty”?'],
  'refund-claims': ['Gdzie widzę sumę odrzuconych prowizji?', 'Jak filtrować wnioski?', 'Jak działa przycisk „Odwołaj”?', 'Co po wysłaniu odwołania?', 'Co oznacza „Rozwiązano”?', 'Do czego służy „✕ Zamknij”?'],
  aiopen: ['Co potrafi AI OPEN?', 'Gdzie kupić AI OPEN?', 'Jak przekazać zamówienie do realizacji?'],
  settings: ['Jak dodać konto Allegro?', 'Jak skonfigurować AI?', 'Jak dodać bazę wiedzy?'],
  'settings:allegro': ['Czy muszę odnawiać tokeny Allegro?'],
  'settings:ai': ['Jak skonfigurować model AI?'],
  'settings:knowledge': ['Jak zasilić AI wiedzą?', 'Gdzie działa Baza Wiedzy AI?', 'Czy mogę edytować wpisy?'],
  'settings:profile': ['Do czego służy zakładka „Moje konto”?'],
  'settings:templates': ['Do czego służą „Wzory odpowiedzi”?'],
  'settings:shortcuts': ['Jak działają „Skróty #”?'],
  'settings:users': ['Co ustawię w „Użytkownicy”?'],
  'settings:asystent': ['Do czego służy zakładka „Asystent AI”?'],
  'refund-claims:appealed': ['Ile trwa rozpatrzenie odwołania?', 'Jak dodać hasło do konta Allegro?', 'Co zrobić gdy odwołanie przepadło?'],
  'refund-claims:resolved': ['Jak przywrócić wniosek?', 'Co oznacza status rozwiązany?', 'Jak zsynchronizować nowe wnioski?'],
  'refund-claims:passwords': ['Jak dodać hasło?', 'Dlaczego hasło jest wymagane?', 'Jak działa automatyczne odwołanie?'],
  'refund-claims:import': ['Jak dodać wnioski ręcznie?', 'Jak zsynchronizować z Allegro?', 'Co wpisać w pole loginów?'],
  returns: ['Zwrot z zamazanej etykiety?', 'Po czym szukać zwrotów?', 'Co wyróżnia widok zwrotu?', 'Jak działają przyciski zwrotu?', 'Jak zlecić zwrot przez Allegro?'],
  'returns:refunds': ['Do czego służą „Wydruki”?', 'Jak pobrać wiele potwierdzeń naraz?', 'Jak znaleźć wpłaty do wydruku?'],
  'offers:promos': ['Co to auto-wyłączanie wyróżnień?', 'Jak ustawić warunki wyłączenia?', 'Warunki dla wielu ofert naraz?'],
  'settings:offers': ['Jak działa alert w „Oferty”?', 'Jakie ostrzeżenia w „Zwroty”?', 'Co to „Profil sprzedażowy”?'],
  'settings:hours': ['Jak działają auto-odpowiedzi?'],
  'email': ['Jak wysłać nowego maila?', 'Jak filtrować nieprzeczytane emaile?', 'Jak dodać regułę automatyczną?', 'Jak przydzielić email do agenta?'],
  'email:thread': ['Jak przetłumaczyć email klienta?', 'Jak dodać załącznik do odpowiedzi?', 'Jak zobaczyć historię obsługi?', 'Jak wysłać odpowiedź w języku klienta?'],
  'email:compose': ['Jak wygenerować treść przez AI?', 'Jak wysłać email po niemiecku?', 'Jak dodać załącznik do maila?', 'Jak zobaczyć podgląd tłumaczenia?'],
  'settings:imap': ['Do czego służą Konta Email?', 'Jak dodać skrzynkę?', 'Jak ustawić SMTP?'],
  'settings:imap:rules': ['Jak dodać regułę automatyczną?', 'Jak przydzielić emaile do agenta automatycznie?', 'Jak zakończyć emaile automatycznie?', 'Jak edytować istniejącą regułę?'],
};

let _appliedContext = '';
// Stan przechowywany poza komponentem - App montuje asystenta osobno w kazdym widoku,
// wiec bez tego przejscie do innego modulu kasowalo rozmowe i zamykalo okno.
let _keepOpen = false;
let _keepMessages: Message[] = [];

function readEnabledMobile(): boolean {
  const val = localStorage.getItem('assistant_enabled_mobile');
  return val === null ? true : val === 'true';
}

function readEnabledDesktop(): boolean {
  const val = localStorage.getItem('assistant_enabled_desktop');
  return val === null ? true : val === 'true';
}

function readMobilePos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem('assistant_pos');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function AIAssistant({ currentModule, isFirstVisit }: Props) {
  const isMobile = window.innerWidth < 768;

  const [open, setOpen] = useState(_keepOpen);
  const [messages, setMessages] = useState<Message[]>(_keepMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [enabled, setEnabled] = useState<boolean>(isMobile ? readEnabledMobile() : readEnabledDesktop());
  const [disabledToast, setDisabledToast] = useState(false);

  const defaultPos = () => {
    const saved = readMobilePos();
    if (saved) return saved;
    return { x: window.innerWidth - 68, y: window.innerHeight - 140 };
  };
  const [iconPos, setIconPos] = useState<{ x: number; y: number }>(defaultPos);
  const isDragging = useRef(false);
  const dragStart = useRef<{ touchX: number; touchY: number; iconX: number; iconY: number } | null>(null);
  const hasMoved = useRef(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevModule = useRef(currentModule);

  const [fullContext, setFullContext] = useState<string>(() => {
    const ctx = localStorage.getItem('assistant_context') || '';
    return ctx || currentModule;
  });

  const getFullContext = (): string => fullContext;

  useEffect(() => {
    let lastCtx = localStorage.getItem('assistant_context') || currentModule;
    let lastEnabled = isMobile ? readEnabledMobile() : readEnabledDesktop();
    let lastReset = localStorage.getItem('assistant_pos_reset') || '';
    let lastOpenReq = localStorage.getItem('assistant_open_request') || '';
    const interval = setInterval(() => {
      const ctx = localStorage.getItem('assistant_context') || currentModule;
      if (ctx !== lastCtx) {
        lastCtx = ctx;
        setFullContext(ctx);
      }
      const nowEnabled = isMobile ? readEnabledMobile() : readEnabledDesktop();
      if (nowEnabled !== lastEnabled) {
        lastEnabled = nowEnabled;
        if (!nowEnabled) {
          setOpen(false);
          setEnabled(false);
          setDisabledToast(true);
          setTimeout(() => setDisabledToast(false), 5000);
        } else {
          setEnabled(true);
          setDisabledToast(false);
        }
      }
      const nowOpenReq = localStorage.getItem('assistant_open_request') || '';
      if (nowOpenReq !== lastOpenReq) {
        lastOpenReq = nowOpenReq;
        if (nowOpenReq) { setEnabled(true); setOpen(true); }
      }
      const nowReset = localStorage.getItem('assistant_pos_reset') || '';
      if (nowReset !== lastReset) {
        lastReset = nowReset;
        const defaultX = window.innerWidth - 68;
        const defaultY = window.innerHeight - 140;
        setIconPos({ x: defaultX, y: defaultY });
      }
    }, 600);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fullContext) return;
    if (fullContext === _appliedContext) return;
    _appliedContext = fullContext;
    const welcome = WELCOME[fullContext] || WELCOME[currentModule] || WELCOME.home;
    setMessages(prev => {
      if (prev.length > 0) {
        const last = prev[prev.length - 1];
        if (last.role === 'assistant' && (last.content === welcome || last.content === '📍 ' + welcome)) {
          return prev;
        }
      }
      const hasUserMsg = prev.some(m => m.role === 'user');
      if (!hasUserMsg) {
        return [{ role: 'assistant', content: welcome }];
      }
      const last = prev[prev.length - 1];
      if (last.role === 'assistant' && last.content.startsWith('📍 ')) {
        return [...prev.slice(0, -1), { role: 'assistant', content: '📍 ' + welcome }];
      }
      return [...prev, { role: 'assistant', content: '📍 ' + welcome }];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullContext]);

  useEffect(() => {
    if (isFirstVisit && enabled) {
      const t = setTimeout(() => {
        setOpen(true);
        const welcome = WELCOME[getFullContext()] || WELCOME[currentModule] || WELCOME.home;
        setMessages([{ role: 'assistant', content: welcome }]);
      }, 2000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentModule !== prevModule.current) {
      prevModule.current = currentModule;
      if (!open) setHasNew(false);
      // Kontekst w localStorage moze pochodzic z poprzedniego modulu (nie kazdy modul go czysci).
      // Podkonteksty (np. "issues:claim" przy module "issues") zostawiamy bez zmian.
      const ctx = localStorage.getItem('assistant_context') || '';
      const pasuje = ctx === currentModule || ctx.startsWith(currentModule + ':');
      if (!pasuje) {
        localStorage.setItem('assistant_context', currentModule);
        setFullContext(currentModule);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModule]);

  // Zachowaj stan okna i rozmowe miedzy przemontowaniami komponentu
  useEffect(() => { _keepOpen = open; }, [open]);
  useEffect(() => { _keepMessages = messages; }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const openChat = () => {
    setOpen(true);
    setHasNew(false);
    if (messages.length === 0) {
      const welcome = WELCOME[getFullContext()] || WELCOME[currentModule] || WELCOME.home;
      setMessages([{ role: 'assistant', content: welcome }]);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg: Message = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      const history = newMessages.slice(-7).map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(`${API}/assistant/chat`, {
        message: text,
        module: currentModule,
        history: history.slice(0, -1),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: e.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie lub napisz na info@openkontakt.com'
      }]);
    }
    setLoading(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    isDragging.current = true;
    hasMoved.current = false;
    dragStart.current = { touchX: touch.clientX, touchY: touch.clientY, iconX: iconPos.x, iconY: iconPos.y };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !dragStart.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStart.current.touchX;
    const dy = touch.clientY - dragStart.current.touchY;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasMoved.current = true;
      e.preventDefault();
    }
    const ICON_SIZE = 48;
    const newX = Math.max(0, Math.min(window.innerWidth - ICON_SIZE, dragStart.current.iconX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - ICON_SIZE, dragStart.current.iconY + dy));
    setIconPos({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    if (!hasMoved.current) {
      openChat();
    } else {
      localStorage.setItem('assistant_pos', JSON.stringify(iconPos));
    }
    dragStart.current = null;
  };

  if (!enabled) {
    if (disabledToast) {
      return (
        <div style={{
          position: 'fixed', bottom: isMobile ? 80 : 24, right: 16,
          background: 'rgba(30,30,30,0.85)', color: '#fff', fontSize: 12,
          padding: '9px 14px', borderRadius: 20, zIndex: 8000, pointerEvents: 'none',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)', maxWidth: 260, lineHeight: 1.5,
        }}>
          Asystent wyłączony — włącz go w <strong>Ustawieniach → Asystent</strong> ⚙️
        </div>
      );
    }
    return null;
  }

  return (
    <>
      {!open && (
        isMobile ? (
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            title="Asystent OPENkontakt"
            style={{
              position: 'fixed', left: iconPos.x, top: iconPos.y,
              width: 48, height: 48, borderRadius: '50%', background: '#1d4ed8',
              color: '#fff', border: 'none', cursor: 'grab', fontSize: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(29,78,216,0.4)', zIndex: 10002,
              userSelect: 'none', touchAction: 'none',
            }}
          >
            {hasNew ? '💬' : '?'}
            {hasNew && (
              <span style={{
                position: 'absolute', top: 0, right: 0, width: 12, height: 12,
                borderRadius: '50%', background: '#ef4444', border: '2px solid #fff',
              }} />
            )}
          </div>
        ) : (
          <button
            onClick={openChat}
            title="Asystent OPENkontakt"
            style={{
              position: 'fixed', bottom: 24, right: 20, width: 48, height: 48,
              borderRadius: '50%', background: '#1d4ed8', color: '#fff', border: 'none',
              cursor: 'pointer', fontSize: 22, display: 'flex', alignItems: 'center',
              justifyContent: 'center', boxShadow: '0 4px 16px rgba(29,78,216,0.4)',
              zIndex: 10002, transition: 'transform 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            {hasNew ? '💬' : '?'}
            {hasNew && (
              <span style={{
                position: 'absolute', top: 0, right: 0, width: 12, height: 12,
                borderRadius: '50%', background: '#ef4444', border: '2px solid #fff',
              }} />
            )}
          </button>
        )
      )}

      {open && (
        <div style={{
          position: 'fixed', bottom: isMobile ? 56 : 24, right: isMobile ? 0 : 20,
          width: isMobile ? '100%' : 340, height: isMobile ? '52dvh' : 460,
          background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
          zIndex: 10002, overflow: 'hidden', border: '1px solid #e5e7eb',
        }}>
          <div style={{
            background: '#1d4ed8', color: '#fff', padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0,
            }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Asystent OPENkontakt</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>Zawsze gotowy do pomocy</div>
            </div>
            <button
              onClick={() => {
                const key = isMobile ? 'assistant_enabled_mobile' : 'assistant_enabled_desktop';
                localStorage.setItem(key, 'false');
                setOpen(false);
                setEnabled(false);
                setDisabledToast(true);
                setTimeout(() => setDisabledToast(false), 5000);
              }}
              title="Wyłącz asystenta"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: 11, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, opacity: 0.9, whiteSpace: 'nowrap' }}
            >Wyłącz</button>
            <button
              onClick={() => { setOpen(false); localStorage.setItem('assistant_seen', '1'); }}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '2px 6px', opacity: 0.8 }}
            >✕</button>
          </div>

          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#f9fafb',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', padding: '8px 12px',
                  borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: m.role === 'user' ? '#1d4ed8' : '#fff',
                  color: m.role === 'user' ? '#fff' : '#374151', fontSize: 13, lineHeight: 1.55,
                  border: m.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {m.role === 'assistant'
                    // eslint-disable-next-line no-useless-escape
                    ? m.content.split(/(\[([^\]]+)\]\((https?:\/\/[^\)]+)\))/).reduce((acc: React.ReactNode[], part, i) => {
                        if (i % 4 === 0) return [...acc, part];
                        if (i % 4 === 1) return [...acc, <a key={i} href={m.content.match(/\(https?:\/\/[^\)]+\)/g)?.[Math.floor(i/4)]?.slice(1,-1) || '#'} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>{m.content.match(/\[[^\]]+\]/g)?.[Math.floor(i/4)]?.slice(1,-1) || part}</a>]; // eslint-disable-line no-useless-escape
                        return acc;
                      }, [])
                    : m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ padding: '8px 14px', borderRadius: '12px 12px 12px 2px', background: '#fff', border: '1px solid #e5e7eb', fontSize: 18, color: '#9ca3af' }}>
                  <span style={{ animation: 'pulse 1s infinite' }}>●</span>
                  <span style={{ animation: 'pulse 1s infinite 0.2s' }}>●</span>
                  <span style={{ animation: 'pulse 1s infinite 0.4s' }}>●</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div style={{ padding: '10px 12px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', gap: 8, flexShrink: 0 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Zadaj pytanie o aplikację..."
              maxLength={500}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'system-ui' }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                padding: '8px 14px',
                background: input.trim() && !loading ? '#1d4ed8' : '#e5e7eb',
                color: input.trim() && !loading ? '#fff' : '#9ca3af',
                border: 'none', borderRadius: 8, fontSize: 13,
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                fontWeight: 600, transition: 'background 0.15s',
              }}
            >↑</button>
          </div>

          {!loading && (
            <div style={{ padding: '0 12px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', background: '#fff', flexShrink: 0 }}>
              {(SUGGESTIONS[getFullContext()] || SUGGESTIONS[currentModule] || SUGGESTIONS.home).map(q => (
                <button
                  key={q}
                  onClick={async () => {
                    setInput('');
                    const userMsg: Message = { role: 'user', content: q };
                    const newMessages = [...messages, userMsg];
                    setMessages(newMessages);
                    setLoading(true);
                    try {
                      const history = newMessages.slice(-7).map(m => ({ role: m.role, content: m.content }));
                      const res = await axios.post(`${API}/assistant/chat`, {
                        message: q, module: getFullContext(), history: history.slice(0, -1),
                      });
                      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
                    } catch (e: any) {
                      setMessages(prev => [...prev, { role: 'assistant', content: e.response?.data?.detail || 'Błąd — spróbuj ponownie' }]);
                    }
                    setLoading(false);
                  }}
                  style={{
                    padding: '4px 10px', border: '1px solid #bfdbfe', borderRadius: 20,
                    background: '#eff6ff', color: '#1d4ed8', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >{q}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
